'use client';

import { useAnalyticsPageView } from '@ria-client/segment/Analytics/AnalyticsPageView';
import { AnalyticsSkuViewed } from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import { FeatureViewed } from '@ria-client/segment/components/FeatureViewed';
import type { MonetizableEnum } from '@ria-client/segment/enums';
import type { FinancialAdvisorsAdvisorProductCardProps } from '@ria-client/supergraph/structuredContent/hydration/FinancialAdvisorsAdvisorProductCard/types';
import { RatingTooltip, RiaDisclaimer, StarRating } from '@ria-client/tw-shared-ui';
import { Percent, Wallet } from 'lucide-react';
import type React from 'react';
import { AdvisorCardDetails } from './financial-advisors-advisor-product-card/card-details';
import { FeeDetails } from './financial-advisors-advisor-product-card/fee-details';
import { NwaBanner } from './financial-advisors-advisor-product-card/nwa-banner';
import { CardCta } from './financial-advisors-product-card/card-cta';
import { NW_RATING_DEFAULT_TOOLTIP } from './lib/financial-advisors/drivers';

const MINT = 'bg-[var(--content-green-extra-light)]';
const STAT_LABEL =
  'flex items-center gap-2 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-muted-foreground';

export function FinancialAdvisorsAdvisorProductCard({
  props,
}: {
  props: FinancialAdvisorsAdvisorProductCardProps;
}): React.JSX.Element {
  const {
    marketplaceEntity: entity,
    showNwaBanner,
    detailsDefaultExpanded = true,
    feeTier1Label,
    feeTier2Label,
    feeTier3Label,
    feeDetailsFootnote,
    prosLabel = 'Best for:',
    consLabel = 'Consider other options if:',
    monetizingCtaLabel,
  } = props;
  const { product } = entity;
  // Cross-page node: without page_type, Shopping Finished defaults to 'roundup'.
  const { pageType } = useAnalyticsPageView();
  const hasRating = entity.starRating != null;

  return (
    <FeatureViewed
      entityName="financial_advisors_advisor_product_card"
      entitySection="structured_content"
    >
      <AnalyticsSkuViewed
        additionalAttributes={pageType ? { page_type: pageType } : undefined}
        impressionPosition={0}
        impressionSection="financial_advisors_advisor_prod_card"
        institutionId={entity.institution.id}
        institutionName={entity.institution.name}
        level="offer"
        monetizable={entity.monetizable}
        name={entity.name}
        offerId={entity.id}
        offerName={entity.name}
        productId={product.id}
        productName={product.name}
        productType={entity.productType}
        source="marketplace"
      >
        {/* Container queries, not viewport breakpoints: the same card sits in full-width and narrow review columns. */}
        <div className="my-4 @container">
          <section
            aria-label={entity.displayName}
            className="overflow-hidden rounded-lg border border-[var(--content-neutral-lighter)] bg-white text-foreground"
          >
            {showNwaBanner ? <NwaBanner displayName={entity.displayName} /> : null}
            <div className="grid gap-4 p-4 @xl:p-5 @3xl:grid-cols-[minmax(0,1fr)_224px] @3xl:gap-6 @3xl:p-6">
              <div className="flex min-w-0 flex-col gap-3 @xl:gap-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 min-w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-white p-1 @xl:h-12 @xl:min-w-12">
                    <img
                      alt={`${entity.displayName} logo`}
                      className="h-full w-auto max-w-24 object-contain"
                      src={entity.image.source}
                    />
                  </span>
                  <h3 className="m-0 line-clamp-2 font-display text-lg leading-tight font-bold @xl:text-xl">
                    {entity.displayName}
                  </h3>
                </div>
                <dl
                  className={`m-0 flex flex-col gap-px overflow-hidden rounded-lg border border-border bg-border @xl:grid ${hasRating ? '@xl:grid-cols-[1.35fr_1fr_1fr]' : '@xl:grid-cols-2'}`}
                >
                  {hasRating ? (
                    <div
                      className={`flex flex-col justify-center gap-1 px-3 py-2.5 @xl:gap-1.5 @xl:px-4 @xl:py-3 ${MINT}`}
                    >
                      <dt className={STAT_LABEL}>
                        NerdWallet rating
                        <RatingTooltip
                          starRatingTooltip={entity.starRatingTooltip ?? NW_RATING_DEFAULT_TOOLTIP}
                        />
                      </dt>
                      <dd className="m-0 flex flex-col gap-1 @xl:gap-1.5">
                        <span className="flex items-center gap-2">
                          <span className="font-display text-2xl leading-none font-bold text-[var(--content-green-dark)] @xl:text-[28px]">
                            {entity.starRating?.toFixed(1)}
                            <span className="font-sans text-xs font-semibold text-muted-foreground">
                              /5
                            </span>
                          </span>
                          <StarRating gap="2px" size="16px" value={entity.starRating} />
                        </span>
                        {/* Marketplace dates already include their labels ("Reviewed in: May 2026"). */}
                        <span className="text-[10px] leading-[14px] text-muted-foreground">
                          {entity.reviewedAtDate ? (
                            <span className="block">{entity.reviewedAtDate}</span>
                          ) : null}
                          {entity.periodConsideredDate ? (
                            <span className="block">{entity.periodConsideredDate}</span>
                          ) : null}
                        </span>
                      </dd>
                    </div>
                  ) : null}
                  <Stat icon={<Percent size={13} strokeWidth={2.5} />} label="Fees">
                    <span className="text-sm leading-snug font-bold @xl:text-base">
                      {entity.driverFees.value}
                    </span>
                    <FeeDetails
                      footnote={feeDetailsFootnote}
                      tiers={[
                        { amount: product.advisorFeeTier1Amt, label: feeTier1Label },
                        { amount: product.advisorFeeTier2Amt, label: feeTier2Label },
                        { amount: product.advisorFeeTier3Amt, label: feeTier3Label },
                      ]}
                    />
                  </Stat>
                  <Stat icon={<Wallet size={13} strokeWidth={2.5} />} label="Asset minimum">
                    <span className="text-sm leading-snug font-bold @xl:text-base">
                      {entity.driverAccountMinimum.value}
                    </span>
                  </Stat>
                </dl>
              </div>
              <div className="flex flex-col items-center justify-center gap-2 @3xl:border-l @3xl:border-border @3xl:pl-6">
                <CardCta marketplaceEntity={entity} monetizingCtaLabel={monetizingCtaLabel} />
                {entity.riaDisclaimerText ? (
                  <RiaDisclaimer
                    isFidelityOffer={entity.isFidelityOffer}
                    monetizable={entity.monetizable as MonetizableEnum}
                    riaDisclaimerText={entity.riaDisclaimerText}
                  />
                ) : null}
              </div>
            </div>
            <AdvisorCardDetails
              consBullets={entity.consBullets}
              consLabel={consLabel}
              defaultExpanded={detailsDefaultExpanded}
              prosBullets={entity.prosBullets}
              prosLabel={prosLabel}
            />
          </section>
        </div>
      </AnalyticsSkuViewed>
    </FeatureViewed>
  );
}

function Stat({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 bg-white px-3 py-2 @xl:flex-col @xl:items-start @xl:justify-start @xl:gap-0 @xl:px-4 @xl:py-3">
      <dt className={`shrink-0 ${STAT_LABEL}`}>
        <span
          aria-hidden
          className={`flex size-5 items-center justify-center rounded-md text-[var(--content-green)] @xl:size-6 ${MINT}`}
        >
          {icon}
        </span>
        {label}
      </dt>
      <dd className="m-0 flex flex-col items-end text-right @xl:mt-1.5 @xl:items-start @xl:text-left">
        {children}
      </dd>
    </div>
  );
}
